class BrandBikesFilter extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-bikes-grid]');
    const seenHref = new Set();
    for (const card of [...this.querySelectorAll('.brand-bikes__card')]) {
      const href = card.querySelector('a')?.getAttribute('href') || '';
      if (href && seenHref.has(href)) card.remove();
      else if (href) seenHref.add(href);
    }
    this.cards = [...this.querySelectorAll('.brand-bikes__card')];
    this.completeControls();
    this.tabs = [...this.querySelectorAll('.brand-bikes__tab')];
    this.kinds = [...this.querySelectorAll('.brand-bikes__kind')];
    this.brands = [...this.querySelectorAll('.brand-bikes__brand')];
    this.emptyNote = this.querySelector('[data-bikes-empty]');

    this.style_ = this.readStyleFromUrl() || 'all';
    this.kind = this.dataset.defaultKind || 'complete';
    this.brand = 'all';

    this.tabs.forEach((t) => t.addEventListener('click', () => this.setStyle(t.dataset.style)));
    this.kinds.forEach((k) => k.addEventListener('click', () => this.setKind(k.dataset.kind)));
    this.brands.forEach((b) => b.addEventListener('click', () => this.setBrand(b.dataset.brand)));

    this.querySelector('.brand-bikes__tabs')?.addEventListener('keydown', (e) => this.onKeydown(e));
    window.addEventListener('popstate', () => {
      this.style_ = this.readStyleFromUrl() || 'all';
      this.apply();
    });

    this.apply();
  }

  completeControls() {
    const LABELS = { road: 'Road', allroad: 'All-Road', gravel: 'Gravel', mountain: 'Mountain',
                     tt: 'Triathlon', track: 'Track', ebike: 'E-Bike', urban: 'Urban' };
    const ORDER = Object.keys(LABELS);

    const tabRow = this.querySelector('.brand-bikes__tabs');
    if (tabRow) {
      const have = new Set([...tabRow.querySelectorAll('.brand-bikes__tab')].map((t) => t.dataset.style));
      const present = new Set(this.cards.map((c) => c.dataset.style));
      for (const style of ORDER) {
        if (!present.has(style) || have.has(style)) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'brand-bikes__tab';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', 'false');
        btn.dataset.style = style;
        btn.textContent = LABELS[style];
        const after = [...tabRow.querySelectorAll('.brand-bikes__tab')]
          .filter((t) => t.dataset.style !== 'all' && ORDER.indexOf(t.dataset.style) > ORDER.indexOf(style))[0];
        after ? tabRow.insertBefore(btn, after) : tabRow.appendChild(btn);
      }
    }

    const brandRow = this.querySelector('.brand-bikes__brands');
    if (brandRow) {
      const have = new Set([...brandRow.querySelectorAll('.brand-bikes__brand')].map((b) => b.dataset.brand));
      const seen = new Map();
      for (const c of this.cards) {
        const slug = c.dataset.brand;
        if (slug && !seen.has(slug)) seen.set(slug, c.querySelector('.brand-bikes__vendor')?.textContent.trim() || slug);
      }
      for (const [slug, label] of [...seen].sort((a, b) => a[1].localeCompare(b[1]))) {
        if (have.has(slug)) continue;
        void 0;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'brand-bikes__brand';
        btn.dataset.brand = slug;
        btn.textContent = label;
        brandRow.appendChild(btn);
      }

      const all = brandRow.querySelector('[data-brand="all"]');
      const rest = [...brandRow.querySelectorAll('.brand-bikes__brand')]
        .filter((b) => b.dataset.brand !== 'all')
        .sort((a, b) => a.textContent.trim().localeCompare(b.textContent.trim()));
      if (all) brandRow.appendChild(all);
      for (const b of rest) brandRow.appendChild(b);
      if (all) brandRow.insertBefore(all, brandRow.firstChild);
    }
  }

  readStyleFromUrl() {
    const p = new URLSearchParams(window.location.search).get('style');
    return p && this.tabs.some((t) => t.dataset.style === p) ? p : null;
  }

  setStyle(style) {
    if (style === this.style_) return;
    this.style_ = style;
    const url = new URL(window.location);
    style === 'all' ? url.searchParams.delete('style') : url.searchParams.set('style', style);
    window.history.replaceState({}, '', url);
    this.apply();
  }

  setBrand(brand) {
    if (brand === this.brand) return;
    this.brand = brand;
    this.apply();
  }

  setKind(kind) {
    if (kind === this.kind) return;
    this.kind = kind;
    this.apply();
  }

  onKeydown(e) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const i = this.tabs.findIndex((t) => t.dataset.style === this.style_);
    let next = i;
    if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
    if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = this.tabs.length - 1;
    this.setStyle(this.tabs[next].dataset.style);
    this.tabs[next].focus();
  }

  apply() {
    let shown = 0;
    for (const card of this.cards) {
      const styleOk = this.style_ === 'all' || card.dataset.style === this.style_;
      const kindOk = this.dataset.showToggle !== 'true' || card.dataset.kind === this.kind;
      const brandOk = this.brand === 'all' || card.dataset.brand === this.brand;
      const visible = styleOk && kindOk && brandOk;
      card.hidden = !visible;
      if (visible) shown += 1;
    }

    for (const t of this.tabs) {
      const on = t.dataset.style === this.style_;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    }

    for (const k of this.kinds) {
      const on = k.dataset.kind === this.kind;
      k.classList.toggle('is-active', on);
      k.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    for (const b of this.brands) {
      const on = b.dataset.brand === this.brand;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    if (this.emptyNote) this.emptyNote.hidden = shown > 0;
  }
}

customElements.define('brand-bikes-filter', BrandBikesFilter);
